var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useContext } from 'react';
import { snowplowFirePageViewEvent } from '@at/track-core';
import { trackComscorePageView, trackUtagPageWhenReady } from '@at/sauron-tracking/lib/utils/index';
import { ATConsumerTrackingStateContext } from '../../tracking-context/ATTracking.context';
import { CURRENT_PAGE_ENTITY_VERSION } from '../../tracking-utils/types.utils';
/**
 * Custom hook to fire a page view event using AT.
 *
 * @param {ATPageViewArgsOnlyPageName} [args={}] - Optional properties to customise the page view event.
 * @param {string} [args.pageName] - The name of the page to be tracked.
 *
 *
 * @returns {Function} - A function to fire the page view event with the specified properties.
 *  @param {boolean} [args.skipTracking] - Flag to skip tracking if set to true.
 *  @param {Function} [args.setPageEntity] - Function to set the current page entity.
 *  @param {Function} [args.withCombinedEntities] - Function to combine entities for tracking.
 *
 * @example
 * const firePageView = useATPageViewEvent({ pageName: 'HomePage' });
 * firePageView({
 *   utagDimensions: { dimension1: 'value1' },
 *   channel: 'cars',
 *   customiseEntities: (currentEntities) => [...currentEntities, { SCHEMA_KEY: 'custom_entity', value: 'custom_value' }]
 * });
 */
export function useATPageViewEvent(args) {
    var _a = useContext(ATConsumerTrackingStateContext), skipTracking = _a.skipTracking, setPageEntity = _a.setPageEntity, withCombinedEntities = _a.withCombinedEntities;
    var topLevelPageName;
    if (args !== undefined && 'pageName' in args) {
        topLevelPageName = args.pageName;
    }
    var firePageView = useCallback(function (_a) {
        var _b, _c, _d;
        var _e = _a === void 0 ? {} : _a, channel = _e.channel, customiseEntities = _e.customiseEntities, _f = _e.pageName, pageName = _f === void 0 ? topLevelPageName : _f, utagDimensions = _e.utagDimensions;
        if (skipTracking)
            return;
        // if pageName prop exists, store updated context page entity for use in other "fire event" actions
        if (pageName) {
            var currentPageEntity = {
                SCHEMA_KEY: CURRENT_PAGE_ENTITY_VERSION,
                name: pageName,
                channel: channel !== null && channel !== void 0 ? channel : 'cars',
            };
            setPageEntity(currentPageEntity);
        }
        var entities = withCombinedEntities([]);
        entities = customiseEntities !== undefined ? customiseEntities(entities) : entities;
        snowplowFirePageViewEvent(entities);
        var getUtagDataFromPageEntity = entities.find(function (entity) { return entity.SCHEMA_KEY === CURRENT_PAGE_ENTITY_VERSION; });
        var pageTrackingData = __assign(__assign({}, utagDimensions), { page_name: (_b = getUtagDataFromPageEntity === null || getUtagDataFromPageEntity === void 0 ? void 0 : getUtagDataFromPageEntity.name) !== null && _b !== void 0 ? _b : 'unknown_page_name', page_name_granular: (_c = getUtagDataFromPageEntity === null || getUtagDataFromPageEntity === void 0 ? void 0 : getUtagDataFromPageEntity.name) !== null && _c !== void 0 ? _c : 'unknown_page_name_granular', channel: (_d = getUtagDataFromPageEntity === null || getUtagDataFromPageEntity === void 0 ? void 0 : getUtagDataFromPageEntity.channel) !== null && _d !== void 0 ? _d : 'unknown_channel' });
        trackComscorePageView();
        trackUtagPageWhenReady(pageTrackingData);
    }, [topLevelPageName, skipTracking, withCombinedEntities, setPageEntity]);
    return { firePageView: firePageView };
}
